import {GenMapLayer} from '../../generated/serverModels/GenMapLayer';
import {LayerLegend} from './legend';
import {PopoverData} from './popover-data';
import {GenPopover} from '../../generated/serverModels/GenPopover';
import {DatePipe, DecimalPipe, PercentPipe, TitleCasePipe} from '@angular/common';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {CompactCurrencyPipe} from '../pipes/compact-currency.pipe';

export class MapLayer<L extends object = BaseLayer> extends GenMapLayer {
  public renderedLayer: L;

  override legend: LayerLegend;

  public selectedAttribute: unknown;

  constructor(mapLayer?: MapLayer) {
    super(mapLayer);

    if (mapLayer) {
      this.renderedLayer = mapLayer.renderedLayer as unknown as L;
      this.legend = new LayerLegend(this.legend);
    }
  }


  public createPopover(properties: Record<string, unknown>, includeAll: boolean = true): PopoverData[] {

    if (this.popoverData.length === 0) {
      return [
        new PopoverData({ displayValue: this.displayName, isTitle: true }),
        new PopoverData({ displayValue: ' ' }),
      ];
    }

    // A callback function to determine if the row should be included in the popup
    const filterCallback = (p: GenPopover) => {
      const validCheck = includeAll ? true : properties[p.propertyName] !== undefined;
      return !p.title && validCheck;
    };

    // A call back that handles all the logic to create the list of PopoverData that is needed to drive the map popover
    const mapCallback = (p: GenPopover) => {
      const numberPipe = new DecimalPipe('en-us');
      const percentPipe = new PercentPipe('en-us');
      const datePipe = new DatePipe('en-us');
      const titlePipe = new TitleCasePipe();
      const currencyPipe = new CompactCurrencyPipe();
      const displayName = p.displayText || titlePipe.transform(p.propertyName);

      let formattedValue: string | null;
      switch (p.type) {
        case 'STRING':
          formattedValue = (properties[p.propertyName] as string) || 'Not Available';
          break;
        case 'NUMBER':
          formattedValue = numberPipe.transform(properties[p.propertyName] as string | number | null | undefined, p.valueFormat || '1.2-2') || 'Not Available';
          break;
        case 'PERCENT':
          formattedValue = percentPipe.transform(properties[p.propertyName] as string | number | null | undefined, '1.2-2') || 'Not Available';
          break;
        case 'DATE':
          formattedValue = datePipe.transform(properties[p.propertyName] as string | Date | null | undefined, 'short') || 'Not Available';
          break;
          case 'CURRENCY':
          formattedValue = currencyPipe.transform(properties[p.propertyName] as number) || 'Not Available';
          break;
        case 'BOOLEAN':

          if(properties[p.propertyName] === null || properties[p.propertyName] === undefined) {
            formattedValue = 'Not Available';
          } else if(properties[p.propertyName] === false) {
            formattedValue = 'false';
          } else {
            formattedValue = 'true';
          }
          break;
        case 'LINK': {
          const linkValue = properties[p.propertyName] as string | null | undefined;
          if(linkValue === null || linkValue === undefined) {
            formattedValue = 'Not Available';
          } else if( !['http', 'https'].some(protocol => linkValue.startsWith(protocol))) {
            formattedValue = `https://${linkValue}`;
          } else {
            formattedValue = linkValue;
          }


          break;
        }

        default:
          formattedValue = (properties[p.propertyName] as string) || 'Not Available';
      }

      const suffix = (properties[p.suffix] as string) || p.suffix || '';
      formattedValue += suffix;

      return new PopoverData({ displayText: displayName, displayValue: formattedValue, link: p.type === 'LINK' });
    };


    const title = this.popoverData.find((p) => p.title);
    let titleRow = new PopoverData({ displayValue: this.displayName, isTitle: true });
    if (title) {
      titleRow = new PopoverData({ displayValue: properties[title.propertyName] as string, isTitle: true });
    }

    const dataRows = this.popoverData
      .filter(filterCallback)
      .sort((a, b) => (a.ordering > b.ordering ? 1 : -1))
      .map(mapCallback)
      .filter((row) => !!row);

    return [titleRow, ...dataRows];
  }


  public downloadFeaturesAsCsv(features?: {properties: Record<string, unknown>}[]): void {
    const formatCsvCell = (cellValue: string | number): string => {
      if (cellValue !== 0 && (!cellValue || cellValue === 'null')) {
        return 'Not Available';
      } else {
        return `"${cellValue.toString().replace(/"/g, '""')}"`;
      }
    };

    const sortedPopover = this.popoverData.sort((a, b) => {
      if (a.ordering > b.ordering) return 1;
      else if (a.ordering < b.ordering) return -1;
      return 0;
    });


    if(features === undefined) {
      features = (this.renderedLayer as VectorLayer<VectorSource>).getSource()?.getFeatures().map(f => {
        return {
          properties: f.getProperties(),
        };
      }) || [];
    }


    const headers = sortedPopover.map(pd => pd.displayText).join();
    const csvData = features.map(feature => {
      const properties = feature.properties;
      return sortedPopover.map(pd => {
        return formatCsvCell(properties[pd.propertyName] as string | number);
      }).join();
    }).join('\n');


    const blob = new Blob([headers + '\n' + csvData], {type: 'text/csv'});
    const url = window.URL.createObjectURL(blob);

    const tag = document.createElement('a');
    tag.download = `current_${this.displayName.toLowerCase().replace(/ /g,'_')}_data.csv`;
    tag.href = url;

    document.body.appendChild(tag);
    tag.click();
    document.body.removeChild(tag);
  }


  public buildAttribution(): string {
    if (!this.attributionTitle) return '';
    if (!this.attributionUrl) return this.attributionTitle;
    return `<a class="tasti-grid-attribution" target="_blank" href="${this.attributionUrl}">${this.attributionTitle}</a>`;
  }
}
